// The authority over one game: it alone holds the full state (decks in order, everyone's hands, the random seed).
// Players play their turn locally on their own view (see Views.js) and send their moves here when they do something
// that cannot be undone (revealing an event, ending the turn). The authority checks the moves by replaying them on the
// full state, applies them, keeps the move log and history, and hands out each player's view.
// Used in process by the client when there is no server (tests, offline play), and by the API.
const GameState = require('../entities/GameState');
const Career = require('./Career');
const Rules = require('../Rules');
const { MoveTypes, Phase } = require('../Moves/MoveTypes');
const { GetAllStats, PlayerChanges, GroupChanges } = require('../Utilities/Stats');
const LeaderNames = require('../Constants/Names/LeaderNames');
const { Redact } = require('./Views');
const { ActiveBlocking, Back, SameValue } = require('../Moves/Decisions');

const clone = (data) => JSON.parse(JSON.stringify(data));
const { MilitaryCardTypes, CivilCardTypes } = require('../Constants/Names/CardTypes');
const MilitaryTypes = Object.values(MilitaryCardTypes);

const { CardRef, ForViewer } = require('../Utilities/CardRefs');

// A "since your last turn" with each event's text as the viewer is to see it (see ForViewer)
const OwnedForViewer = (since, viewer) => since && { ...since,
    events: since.events.map(event => ({ ...event, text: ForViewer(event.text, viewer) })) };
const { Age } = require('../Constants/Constants');

// Decisions recorded in an event's report without words: what they did shows in its changes
const SilentChoices = ['discardMilitary'];
// Choices shown in the recap only by what they changed
const TextlessChoices = ['gainOne'];
// Choices told in the recap in their own words, with the cards named drawn as cards: (playerBefore, value) => text
const RecapTexts = {
    // Border Conflict, Terrorism: the building destroyed
    destroy: (player, value) => `destroys ${CardRef({ type: CivilCardTypes.Technology, name: value, age: player.GetTechnology(value)?.age })}`,
    // Development of Planning: the event chosen
    planning: (player, value) => `chose ${CardRef({ type: MilitaryCardTypes.Event, name: value, age: Age.AgeA })}`,
    // Freedom of Movement: from which card to which (what it cost shows in the changes)
    moveWorker: (player, value) => value === null ? 'moves no worker'
        : `moves a worker: ${[value.from, value.to].map(name => CardRef({ type: CivilCardTypes.Technology, name, age: player.GetTechnology(name).age })).join(' → ')}`,
    // International Negotiations: what the strongest takes (who loses and gains it shows in the changes)
    negotiate: (player, value) => `chooses 3 ${value}`,
};
// Choices about what happens to others, told on a line of their own so they don't read as the chooser's own change
const ApartChoices = ['negotiate'];

class Authority {
    constructor(gameStateData)
    {
        this.state = new GameState(clone(gameStateData));
        this.version = 0;
        this.committedMoves = [];
        // Finalized moves, oldest first: { player, round, description, at (when committed, if known) }
        this.journal = [];
        // For the "since your last turn" summary:
        //   revealedEvents  - every event revealed, with what it did to each civilization
        //   turnEnds        - per player, everyone's stats and the number of revealed events when that player last ended a turn
        //   turnStartStats  - everyone's stats when the current turn started
        //   turnStartEvents - the number of revealed events when the current turn started
        this.revealedEvents = [];
        //   eventDecisions  - by decision id, the revealed event's report that decision is a choice for
        this.eventDecisions = new Map();
        this.turnStartStats = GetAllStats(this.state);
        // Until a player has ended a turn, their "since your last turn" goes back to when the game began (here)
        this.turnEnds = Object.fromEntries(this.state.playerStates.map(player =>
            [player.name, { stats: this.turnStartStats, events: 0, gameStart: true }]));
        this.turnStartEvents = 0;
        // What each player knows about military cards: every military card in the game by id, the ones each player
        // has held (they know where those went), and the ones everyone has seen (revealed events, tactics in play)
        this.militaryCards = new Map();
        const data = this.state.toData();
        [data.militaryDeck, data.militaryDiscard ?? [], data.currentEvents, data.futureEvents, data.publicTactics ?? [],
            ...data.playerStateData.map(player => player.militaryHand), ...(data.upcomingDecks ?? []).map(deck => deck.military)]
            .flat().forEach(card => this.militaryCards.set(card.id, card));
        this.seenBy = this.state.playerStates.map(() => new Set());
        this.publicCards = new Set();
        // Top current events a player has looked at (Joan of Arc), shown to them face up while still on top; and what
        // only they were told, for their "since your last turn": [{ position (in revealedEvents), report }]
        this.peekedBy = this.state.playerStates.map(() => new Set());
        this.privateNotes = this.state.playerStates.map(() => []);
        this.peekedTurn = null;
        // The military hand Ian Fleming last looked at, by viewer: { target, ids }, dropped once that hand changes
        this.flemingViews = this.state.playerStates.map(() => null);
        this.UpdateKnowledge();
        // The game as it was when each open decision was asked, by decision id: what its player sees until they answer
        this.snapshots = {};
        this.TakeSnapshots();
        this.listeners = [];
        // What each player has done over the game, for their stats (see Career.js)
        this.career = new Career(this.state);
    }

    // Applies a player's moves, in order, if every one is legal; nothing changes otherwise. Returns { ok, error }.
    // at: when they were committed (the server's UTC "YYYY-MM-DD HH:MM:SS"), for the journal
    Commit(playerNumber, moves, at = null)
    {
        if (!Array.isArray(moves) || moves.length === 0)
        {
            return { ok: false, error: "No moves to apply" };
        }
        if (moves.some(move => move.player !== playerNumber))
        {
            return { ok: false, error: "Moves can only be made for yourself" };
        }
        // Check the whole sequence before changing anything
        try {
            Rules.Replay(this.state.toData(), moves);
        } catch (e) {
            return { ok: false, error: e.message };
        }
        moves.forEach(move => {
            const before = this.state;
            const { after, description } = Rules.Step(before, move);
            const decision = move.type === MoveTypes.Decide ? before.decisions.find(d => d.id === move.decisionId) : null;
            if (decision && move.value === Back && decision.back)
            {
                // Going back to choose again: neither the choice taken back nor the going back is worth a log line
                this.journal = this.journal.filter(entry => entry.decisionId !== decision.back.id);
                this.revealedEvents.forEach(report => {
                    if (report.choices) report.choices = report.choices.filter(choice => choice.decisionId !== decision.back.id);
                });
            } else {
                this.journal.push({ player: before.playerStates[move.player].name, age: before.age, round: before.round, description,
                    ...(at ? { at } : {}), ...(decision ? { decisionId: decision.id } : {}),
                    // (where a turn ends, so the next one's entries can be told apart: the end-turn dialog's summary)
                    ...(move.type === MoveTypes.EndTurn ? { turnEnd: true } : {}) });
            }
            this.state = after;
            this.RecordHistory(before, move, decision, at);
            this.career.Note(after);
        });
        this.committedMoves.push(...moves);
        this.version++;
        this.UpdateKnowledge();
        this.TakeSnapshots();
        this.listeners.forEach(listener => listener(this.version));
        return { ok: true };
    }

    // Called with the new version after every commit
    Subscribe(listener)
    {
        this.listeners.push(listener);
        return () => { this.listeners = this.listeners.filter(other => other !== listener); };
    }

    // What a player may see (see Views.js), with the move log and the current player's "since your last turn".
    // viewer null: everything (offline play on one screen, and tests).
    // A player with decisions to make sees the game as it was when the oldest was asked, with only that decision, so
    // they choose knowing what they knew then (and learn nothing of what happened since until they have chosen).
    ViewFor(viewer = null)
    {
        const open = viewer === null ? [] : this.state.decisions.filter(d => d.player === viewer).sort((a, b) => a.id - b.id);
        // (A decision waiting its turn behind an earlier blocking one has no snapshot yet: the live game is shown)
        const snapshot = (open.length > 0 && this.snapshots[open[0].id]) || null;
        const data = snapshot ? JSON.parse(JSON.stringify(snapshot.data)) : this.state.toData();
        if (snapshot)
        {
            // Of the decisions open then, only the ones still open; of the viewer's own, only the one to make now
            const stillOpen = new Set(this.state.decisions.map(d => d.id));
            data.decisions = data.decisions.filter(d => stillOpen.has(d.id) && (d.player !== viewer || d.id === open[0].id));
        }
        return {
            version: this.version,
            viewer,
            // Seeing the game as it was at a decision, when that is behind the present
            frozen: snapshot !== null && snapshot.version !== this.version,
            state: viewer === null ? data : Redact(data, viewer, this.KnowledgeFor(viewer)),
            // (cards face down to all but their owner, e.g. military cards discarded at a new age: face up for them)
            journal: (snapshot ? this.journal.slice(0, snapshot.journalLength) : this.journal)
                .map(entry => ({ ...entry, description: ForViewer(entry.description, viewer) })),
            sinceLastTurn: OwnedForViewer(snapshot ? snapshot.sinceLastTurn : this.SinceLastTurn(viewer), viewer),
            // Once the game is over, who prepared which events (secret until then)
            ...(this.state.finished ? { prepared: this.PreparedEvents() } : {}),
        };
    }

    // Each player's prepared events, in order, and how many non-events (Confucius) they prepared: [{ player, events, nonEvents }]
    PreparedEvents()
    {
        return this.career.Summary().map((career, number) => ({ player: this.state.playerStates[number].name,
            events: career.prepared, nonEvents: career.nonEvents }));
    }

    // Remembers the game as it is now for each decision just asked (see ViewFor)
    TakeSnapshots()
    {
        const open = new Set(this.state.decisions.map(d => d.id));
        // Taken when a decision can be made: a blocking one waiting behind another gets its snapshot once that is made,
        // so its player sees the earlier answers (a player with several decisions sees each after the one before)
        const active = ActiveBlocking(this.state);
        const fresh = this.state.decisions.filter(d => !this.snapshots[d.id] && (!d.blocking || d.id === active?.id));
        if (fresh.length > 0)
        {
            // (each decision's player with their own "since your last turn", as it was then)
            const snapshot = { version: this.version, data: this.state.toData(), journalLength: this.journal.length };
            fresh.forEach(d => { this.snapshots[d.id] = { ...snapshot, sinceLastTurn: this.SinceLastTurn(d.player) }; });
        }
        // Answered decisions no longer need theirs
        Object.keys(this.snapshots).filter(id => !open.has(Number(id))).forEach(id => delete this.snapshots[id]);
    }

    // After an irreversible move: remember revealed events, and at the end of a turn everyone's stats
    RecordHistory(before, move, decision = null, at = null)
    {
        // What the move reports after a turn ends (the next turn's start: a war resolved, Age IV) comes after that turn
        const beforeReports = this.revealedEvents.length;
        if (move.type === MoveTypes.PrepareEvent)
        {
            this.NostradamusSees(before, move);
            const card = before.playerStates[move.player].militaryHand.find(c => c?.id === move.cardId);
            if (card)
            {
                this.career.NotePrepared(move.player, card);
            }
        }
        if (move.type === MoveTypes.PrepareEvent && this.state.lastEvent)
        {
            // The decisions it asks are its players' choices, added to it as they are made, after any made for them at once
            // (A territory: who bids, in order, for the bids' grid)
            const bidders = this.state.auction?.order.map(number => this.state.playerStates[number].name);
            const report = { ...this.state.lastEvent, choices: [...(this.state.lastEvent.choices ?? [])], ...(bidders ? { bidders } : {}) };
            this.revealedEvents.push(report);
            // (not what the revealing player's action phase, starting with the same move, asks, e.g. Boudica)
            this.NoteEventDecisions(before, report, decision => decision.event === report.name);
            if (this.state.lastEvent.id !== undefined)
            {
                this.publicCards.add(this.state.lastEvent.id);
            }
        }
        if (decision && !(move.value === Back && decision.back))
        {
            this.RecordChoice(before, decision, move.value);
        }
        // A choice for an event that asks more (the next bid, paying for the colony, choosing again) asks them for it too
        if (decision && this.ReportFor(decision))
        {
            this.NoteEventDecisions(before, this.ReportFor(decision));
        }
        if (decision?.type === 'flemingPeek')
        {
            this.FlemingSees(decision, move.value);
        }
        // Military cards shuffled back into the deck: whoever knew where they were (held, discarded them) no longer does
        (this.state.reshuffled ?? []).forEach(id => {
            this.seenBy.forEach(seen => seen.delete(id));
            this.publicCards.delete(id);
        });
        // Other things worth everyone's "since your last turn" (e.g. how an aggression turned out)
        this.revealedEvents.push(...(this.state.reports ?? []));
        // Some are worth a journal line as well (journal: whose it is), e.g. a political phase skipped
        (this.state.reports ?? []).filter(report => report.journal).forEach(report =>
            this.journal.push({ player: report.journal, age: report.age ?? this.state.age, round: report.round, description: report.text,
                ...(at ? { at } : {}), ...(report.newAge ? { newAge: true, revealed: report.revealed } : {}) }));
        // Cards a report shows everyone (e.g. a declined pact back in its offerer's hand)
        (this.state.reports ?? []).forEach(report => (report.revealCards ?? []).forEach(id => this.publicCards.add(id)));
        if (move.type === MoveTypes.EndTurn)
        {
            const stats = GetAllStats(this.state);
            this.turnEnds[before.playerStates[move.player].name] = { stats, events: beforeReports };
            this.turnStartStats = stats;
            this.turnStartEvents = beforeReports;
        }
    }

    // The revealed event's report a decision belongs to (asked by it, or by a choice before it), if any. Decisions asked
    // later by something else (e.g. Boudica at the start of an action phase) are not its choices.
    ReportFor(decision)
    {
        return this.eventDecisions.get(decision.id);
    }

    // The decisions the move just made asked (those that pass the filter) are choices for this report
    NoteEventDecisions(before, report, filter = () => true)
    {
        this.state.decisions.filter(decision => decision.id >= before.nextDecisionId && filter(decision))
            .forEach(decision => this.eventDecisions.set(decision.id, report));
    }

    // Whether a revealed event still has decisions of its own to be made
    StillDeciding(report)
    {
        return this.state.decisions.some(decision => decision.event === report.name && this.ReportFor(decision) === report);
    }

    // A choice made for a revealed event (asked by it, or by a choice before it): what the player chose and what it did,
    // added to the event's report so "since your last turn" shows each player's choice
    RecordChoice(before, decision, value)
    {
        const report = this.ReportFor(decision);
        if (!report)
        {
            return;
        }
        const name = before.playerStates[decision.player].name;
        const option = decision.options?.find(o => SameValue(o.value, value));
        // A listed answer by its label (with its section, e.g. "Build: Religion"); otherwise what it did
        const logged = this.state.triggerLog.find(line => line.startsWith(`${decision.event}: ${name}`));
        // (Choices whose effect the changes already say, e.g. which military cards were discarded, need no words)
        const text = SilentChoices.includes(decision.type) ? ''
            : option
                ? (option.section && option.section !== option.label ? `${option.section}: ${option.label}` : option.label)
                : (logged ?? '').replace(`${decision.event}: ${name}`, '').replace(/^[:\s]+/, '') || 'decided';
        // (with the decision's kind and answer, for choices shown in their own way, e.g. a colonization auction's bids)
        // (Paying for a colony: what was sacrificed, and how it adds up, on a line of its own; the choice is the colonizing)
        const payment = decision.type === 'colonyPay' ? require('../Moves/Colonization').PaymentBreakdown(before.playerStates[decision.player], value) : null;
        // (what the colony gave shows in the changes, and the territory as its card)
        const colonized = payment ? 'colonizes' : null;
        // International Agreement: one line for all the cards taken, as cards (not their cost, nor the order), and that
        // the card row was refilled
        if (decision.type === 'agreementTake' && value !== 'decline')
        {
            let taking = report.choices.find(choice => choice.type === 'agreementTake' && choice.player === name);
            if (!taking)
            {
                taking = { decisionId: decision.id, player: name, type: 'agreementTake', cards: [], refilled: false };
                report.choices.push(taking);
            }
            const card = value === 'done' ? null : before.civilCardRow.find(c => c?.id === value?.cardId);
            if (card) taking.cards.push(CardRef(card));
            if (value === 'done') taking.refilled = true;
            taking.text = `${taking.cards.length > 0 ? `takes ${taking.cards.join(' ')}` : 'takes nothing'}${taking.refilled ? '; the card row is refilled' : ''}`;
            return;
        }
        // (A choice whose result is all in the changes, e.g. Development of Markets' food or resources: no words for it)
        const shown = TextlessChoices.includes(decision.type) ? ''
            : RecapTexts[decision.type] ? RecapTexts[decision.type](before.playerStates[decision.player], value) : text;
        report.choices.push({ decisionId: decision.id, player: name, text: colonized ?? shown, type: decision.type,
            ...(ApartChoices.includes(decision.type) ? { apart: true } : {}),
            ...(payment ? { territory: before.auction.territory } : {}),
            ...(decision.type === 'colonyBid' ? { value } : {}), ...(payment ? { payment: { sacrificed: payment.sacrificed, sacrificeTotal: payment.sacrificeTotal, bonuses: payment.bonuses, total: payment.total } } : {}) });
        // And what it changed, with the event's other changes
        PlayerChanges(GetAllStats(before), GetAllStats(this.state)).forEach(({ player, changes }) => {
            const entry = report.changes.find(e => e.player === player) ?? (report.changes.push({ player, changes: [] }), report.changes.at(-1));
            changes.forEach(change => {
                const same = entry.changes.find(c => c.stat === change.stat);
                if (same) same.change += change.change; else entry.changes.push({ ...change });
            });
            entry.changes = entry.changes.filter(c => c.change !== 0);
        });
        report.changes = report.changes.filter(e => e.changes.length > 0);
    }

    // Each player knows the military cards they hold (and so where they go: discarded, prepared as events, played);
    // everyone knows the tactics in play
    UpdateKnowledge()
    {
        this.state.playerStates.forEach((player, index) => {
            player.militaryHand.forEach(card => this.seenBy[index].add(card.id));
            if (player.exclusiveTactic)
            {
                this.publicCards.add(player.exclusiveTactic.id);
            }
        });
        this.state.publicTactics.forEach(card => this.publicCards.add(card.id));
        this.JoanPeek();
    }

    // Joan of Arc: at the beginning of her player's political phase, they look at the top current event (once a turn)
    JoanPeek()
    {
        const state = this.state;
        const player = state.GetCurrentPlayerState();
        const turn = `${state.round}:${state.currentPlayer}`;
        if (state.phase !== Phase.Political || player.leader?.name !== LeaderNames.JoanOfArc || this.peekedTurn === turn)
        {
            return;
        }
        this.peekedTurn = turn;
        const top = state.currentEvents[0];
        if (top)
        {
            this.seenBy[player.playerNumber].add(top.id);
            this.peekedBy[player.playerNumber].add(top.id);
        }
        this.privateNotes[player.playerNumber].push({
            position: this.revealedEvents.length,
            report: { kind: 'notice', private: true, name: LeaderNames.JoanOfArc, round: state.round, changes: [],
                text: top ? `Joan of Arc sees ${require('../Utilities/CardRefs').CardRef(top)} on top of the current events.` : "Joan of Arc: there are no current events to look at." },
        });
    }

// Ian Fleming: his player looks at one player's military cards (they stay known to them, face up, until reshuffled),
    // told only to them
    FlemingSees(decision, target)
    {
        if (target === null || target === undefined)
        {
            return;
        }
        const other = this.state.playerStates[target];
        // Shown face up to him only while that hand stays exactly as it was (see FlemingShown); not known for good
        this.flemingViews[decision.player] = { target, ids: other.militaryHand.map(card => card.id) };
        const cards = other.militaryHand.map(card => CardRef(card)).join(' ');
        // (everyone is told whose cards were looked at, so the one looked at knows; only he sees them)
        const looker = this.state.playerStates[decision.player];
        this.revealedEvents.push({ kind: 'notice', name: LeaderNames.IanFleming, round: this.state.round, changes: [],
            text: `${looker.name} (Ian Fleming) looks at ${other.name}'s military cards.` });
        this.privateNotes[decision.player].push({
            position: this.revealedEvents.length,
            report: { kind: 'notice', private: true, name: LeaderNames.IanFleming, round: this.state.round, changes: [],
                text: other.militaryHand.length ? `Ian Fleming sees ${other.name}'s military cards: ${cards}` : `Ian Fleming: ${other.name} has no military cards.` },
        });
    }

    // Nostradamus: when another player prepares an event, his player looks at it (the card stays face up for them, and
    // only they are told what it is)
    NostradamusSees(before, move)
    {
        const preparer = before.playerStates[move.player];
        const card = preparer.militaryHand.find(c => c?.id === move.cardId);
        if (!card)
        {
            return;
        }
        this.state.playerStates.filter(player => player !== this.state.playerStates[move.player] && !player.resigned
            && player.leader?.name === LeaderNames.Nostradamus).forEach(player => {
            this.seenBy[player.playerNumber].add(card.id);
            this.privateNotes[player.playerNumber].push({
                position: this.revealedEvents.length,
                report: { kind: 'notice', private: true, name: LeaderNames.Nostradamus, round: this.state.round, changes: [],
                    text: `Nostradamus sees the event ${preparer.name} prepared: ${CardRef(card)}.` },
            });
        });
    }

// The cards Ian Fleming's player sees in the hand he looked at: all of them while it is exactly as it was then; once
    // anything about it changes (a card drawn, played or discarded), none, for good
    FlemingShown(viewer)
    {
        const view = this.flemingViews?.[viewer];
        if (!view)
        {
            return [];
        }
        const now = this.state.playerStates[view.target].militaryHand.map(card => card.id);
        if (now.length !== view.ids.length || now.some(id => !view.ids.includes(id)))
        {
            this.flemingViews[viewer] = null;
            return [];
        }
        return view.ids;
    }

    // What the viewer knows about military cards: the ids they know, and what could be in the military deck — the
    // current age's military cards less those they know are elsewhere (a card counted could still be elsewhere)
    KnowledgeFor(viewer)
    {
        const seen = new Set([...this.seenBy[viewer], ...this.publicCards, ...this.FlemingShown(viewer)]);
        const counts = new Map();
        if (this.state.militaryDeck.length > 0)
        {
            [...this.militaryCards.values()]
                .filter(card => card.age === this.state.age && !seen.has(card.id) && MilitaryTypes.includes(card.type))
                .forEach(card => {
                    const key = `${card.type}|${card.name}`;
                    const entry = counts.get(key) ?? { type: card.type, name: card.name, age: card.age, count: 0 };
                    entry.count++;
                    counts.set(key, entry);
                });
        }
        return {
            seen,
            peeked: this.peekedBy[viewer],
            candidates: [...counts.values()].sort((a, b) => a.type.localeCompare(b.type) || a.name.localeCompare(b.name)),
        };
    }

    // What the viewer has not seen yet: events revealed since their last turn (including any revealed now), and how
    // each civilization's numbers changed since then: for the current player, up to the start of this turn (their own
    // moves are in front of them); for anyone else, up to now. Each player sees their own, with what only they were
    // told (Joan of Arc). Before a player's first turn ends it goes back to when the game began.
    // viewer null (offline play on one screen, and tests): the current player's.
    SinceLastTurn(viewer = null)
    {
        const current = this.state.GetCurrentPlayerState();
        const whom = this.state.playerStates[viewer] ?? current;
        const player = whom.name;
        const lastTurn = this.turnEnds[player];
        const names = this.state.playerStates.map(p => p.name);
        const from = lastTurn ? lastTurn.events : this.turnStartEvents;
        const own = this.privateNotes[whom.playerNumber].filter(note => note.position >= from);
        const upTo = whom === current ? this.turnStartStats : GetAllStats(this.state);
        // In the order they happened: each private note before the public reports that came after it
        // (an event still waiting on its decisions, e.g. a territory's auction, only once they are all made: until then
        // it is in the decision area)
        const events = this.revealedEvents.slice(from).map((event, index) => ({ position: from + index, order: 1, event }))
            .filter(({ event }) => !this.StillDeciding(event));
        own.forEach(note => events.push({ position: note.position, order: 0, event: note.report }));
        events.sort((a, b) => a.position - b.position || a.order - b.order);
        // Players with identical changes share a line ("All players" when everyone had the same outcome)
        return {
            player,
            hasPreviousTurn: lastTurn !== undefined && !lastTurn.gameStart,
            sinceGameStart: lastTurn?.gameStart === true,
            events: events.map(({ event }) => ({ ...event, groups: GroupChanges(event.changes, names) })),
            changes: lastTurn ? GroupChanges(PlayerChanges(lastTurn.stats, upTo, true), names) : [],
        };
    }
}

module.exports = Authority;
